import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Pencil, Trash2, ArrowRightLeft } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';

interface Branch {
  id: string;
  slug: string;
  legal_name: string;
  trade_name: string | null;
  cnpj: string | null;
  timezone: string;
  cme_class: string;
  rt_name: string | null;
  rt_coren: string | null;
  anvisa_license: string | null;
  license_expires_at: string | null;
  active: number;
  org_type: 'HQ' | 'BRANCH' | 'STANDALONE';
  parent_org_id: string | null;
}

export function FiliaisPage() {
  const { hasRole, user, switchOrg } = useAuth();
  const canWrite = hasRole('ADMIN');
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiGet<{ items: Branch[]; hq_id?: string }>('/v1/branches'),
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [confirming, setConfirming] = useState<Branch | null>(null);
  const [form, setForm] = useState<any>({});
  const [error, setError] = useState<string | null>(null);

  const createM = useMutation({
    mutationFn: (body: any) => apiPost('/v1/branches', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['branches'] }); setCreating(false); setError(null); setForm({}); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => apiPatch(`/v1/branches/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['branches'] }); setEditing(null); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/branches/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['branches'] }); setConfirming(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });

  const hq = list.data?.items.find((o) => o.org_type === 'HQ');
  const branches = list.data?.items.filter((o) => o.org_type === 'BRANCH') ?? [];
  const isStandalone = list.data?.items.length === 1 && list.data.items[0]?.org_type === 'STANDALONE';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Matriz e filiais"
          subtitle="Hierarquia de organizações sob a mesma conta ICTUS"
          action={<Building2 className="h-5 w-5 text-brand-500" />}
        />
        <CardBody>
          {list.isLoading ? (
            <p className="text-sm text-neutral-500">Carregando...</p>
          ) : (
            <div className="space-y-4">
              {hq ? (
                <div className="rounded-md border border-brand-200 bg-brand-50/50 p-3 dark:border-brand-800 dark:bg-brand-900/30">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{hq.trade_name || hq.legal_name}</span>
                        <Badge tone="brand">MATRIZ</Badge>
                      </div>
                      <div className="text-xs text-neutral-500">slug: {hq.slug} · CNPJ: {hq.cnpj || '—'}</div>
                    </div>
                    {hq.id !== user?.org_id ? (
                      <Button size="sm" variant="ghost" leadingIcon={<ArrowRightLeft className="h-4 w-4" />} onClick={() => switchOrg(hq.id)}>
                        Acessar
                      </Button>
                    ) : <Badge tone="success">ativa</Badge>}
                  </div>
                </div>
              ) : isStandalone ? (
                <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                  Esta organização ainda é STANDALONE. Ao criar a primeira filial, ela será promovida automaticamente a MATRIZ.
                </div>
              ) : null}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Filiais ({branches.length})</h3>
                  {canWrite ? (
                    <Button size="sm" leadingIcon={<Plus className="h-4 w-4" />} onClick={() => { setForm({ timezone: 'America/Sao_Paulo', cme_class: 'II' }); setCreating(true); setError(null); }}>
                      Nova filial
                    </Button>
                  ) : null}
                </div>
                {branches.length === 0 ? (
                  <p className="rounded border border-dashed border-neutral-300 p-4 text-center text-sm text-neutral-500 dark:border-neutral-700">
                    Nenhuma filial cadastrada.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xxs uppercase tracking-wider text-neutral-500">
                        <tr className="border-b border-neutral-200 dark:border-neutral-700">
                          <th className="px-2 py-2 text-left">Razão social</th>
                          <th className="px-2 py-2 text-left">Slug</th>
                          <th className="px-2 py-2 text-left">CNPJ</th>
                          <th className="px-2 py-2 text-left">RT</th>
                          <th className="px-2 py-2 text-left">Licença</th>
                          <th className="px-2 py-2 text-left">Status</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {branches.map((b) => (
                          <tr key={b.id} className="border-b border-neutral-100 dark:border-neutral-800">
                            <td className="px-2 py-2">
                              <div className="font-medium text-neutral-800 dark:text-neutral-100">{b.trade_name || b.legal_name}</div>
                              {b.trade_name ? <div className="text-xxs text-neutral-500">{b.legal_name}</div> : null}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs">{b.slug}</td>
                            <td className="px-2 py-2 text-xs">{b.cnpj || '—'}</td>
                            <td className="px-2 py-2 text-xs">{b.rt_name || '—'}{b.rt_coren ? ` (COREN ${b.rt_coren})` : ''}</td>
                            <td className="px-2 py-2 text-xs">{b.license_expires_at ? `vence ${b.license_expires_at.slice(0,10)}` : '—'}</td>
                            <td className="px-2 py-2">
                              {b.active ? <Badge tone="success">ativa</Badge> : <Badge tone="neutral">inativa</Badge>}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" leadingIcon={<ArrowRightLeft className="h-3 w-3" />} onClick={() => switchOrg(b.id)} title="Acessar filial">
                                  Acessar
                                </Button>
                                {canWrite ? (
                                  <>
                                    <Button size="sm" variant="ghost" onClick={() => { setForm(b); setEditing(b); setError(null); }} title="Editar">
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setConfirming(b)} title="Desativar">
                                      <Trash2 className="h-3 w-3 text-danger" />
                                    </Button>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {creating ? (
        <Modal
          open
          title="Nova filial"
          onClose={() => setCreating(false)}
          size="lg"
          footer={
            <>
              <Button variant="secondary" onClick={() => setCreating(false)}>Cancelar</Button>
              <Button onClick={() => createM.mutate(stripEmpty(form))} loading={createM.isPending}>Criar filial</Button>
            </>
          }
        >
          <BranchForm form={form} setForm={setForm} create error={error} />
        </Modal>
      ) : null}

      {editing ? (
        <Modal
          open
          title={`Editar filial: ${editing.legal_name}`}
          onClose={() => setEditing(null)}
          size="lg"
          footer={
            <>
              <Button variant="secondary" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={() => updateM.mutate({ id: editing.id, body: stripEmpty(form, ['slug','admin_email','admin_password','admin_full_name']) })} loading={updateM.isPending}>Salvar</Button>
            </>
          }
        >
          <BranchForm form={form} setForm={setForm} error={error} />
        </Modal>
      ) : null}

      {confirming ? (
        <Modal
          open
          title="Desativar filial"
          onClose={() => setConfirming(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)}>Cancelar</Button>
              <Button variant="danger" onClick={() => deleteM.mutate(confirming.id)} loading={deleteM.isPending}>Desativar</Button>
            </>
          }
        >
          <p className="text-sm">
            Confirma desativar a filial <strong>{confirming.legal_name}</strong>? Usuários não conseguirão mais operar nela até reativação manual via banco de dados.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}

function stripEmpty(form: any, drop: string[] = []) {
  const out: any = {};
  for (const [k, v] of Object.entries(form)) {
    if (drop.includes(k)) continue;
    if (v === '' || v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

function BranchForm({ form, setForm, create, error }: { form: any; setForm: (f: any) => void; create?: boolean; error: string | null }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Input label="Razão social" value={form.legal_name ?? ''} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} required />
      <Input label="Nome fantasia" value={form.trade_name ?? ''} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} />
      {create ? (
        <Input label="Slug (URL única)" value={form.slug ?? ''} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} required hint="apenas letras minúsculas, números e hífen" />
      ) : (
        <Input label="Slug" value={form.slug ?? ''} disabled />
      )}
      <Input label="CNPJ" value={form.cnpj ?? ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" />
      <Input label="Fuso horário (IANA)" value={form.timezone ?? ''} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/Sao_Paulo" />
      <Input label="Classe CME" value={form.cme_class ?? ''} onChange={(e) => setForm({ ...form, cme_class: e.target.value })} hint="I ou II (RDC 15)" />
      <div className="sm:col-span-2 border-t border-neutral-200 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700">Responsável Técnico</div>
      <Input label="Nome do RT" value={form.rt_name ?? ''} onChange={(e) => setForm({ ...form, rt_name: e.target.value })} />
      <Input label="COREN" value={form.rt_coren ?? ''} onChange={(e) => setForm({ ...form, rt_coren: e.target.value })} />
      {create ? (
        <>
          <div className="sm:col-span-2 border-t border-neutral-200 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-neutral-700">Administrador inicial (opcional)</div>
          <Input label="E-mail do admin" type="email" value={form.admin_email ?? ''} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
          <Input label="Nome do admin" value={form.admin_full_name ?? ''} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} />
          <Input label="Senha inicial" type="password" value={form.admin_password ?? ''} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} hint="mínimo 10 caracteres" />
        </>
      ) : null}
      {error ? <p className="sm:col-span-2 text-sm text-danger">{error}</p> : null}
    </div>
  );
}
